import { endOfMonth, startOfMonth } from 'date-fns';

import type { FROM, SORT, TO } from 'constants/FilterConstants';
import { OVERCOST, PROVIDERS } from 'constants/FilterConstants';
import type { Action } from 'modules/global/generic.types';

import { filterSliceAdapter } from 'modules/global/reducers/filterSliceAdapter';
import type { GlobalConfig } from 'modules/global/reducers/reducers.types';
import * as actionTypes from 'modules/metrics/actionTypes';
import { BILLING_TYPE, DEFAULT_SORT, type BillingEntityEnum } from 'modules/metrics/constants';
import { createDictionary } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

/**
 * The shared `GlobalConfig`, with `matcher` required: every action of this module carries the
 * entity it belongs to in its meta, so each slice is always built with its own matcher.
 */
export type EntityMatcherConfig = Required<Pick<GlobalConfig, 'matcher'>>;

/** The filter set of one channel — the closed set of fields the dashboard can dispatch. */
export type MetricsFiltersReducer = {
  [FROM]: string;
  [TO]: string;
  [SORT]: string;
  /** Message channel only. */
  [PROVIDERS]?: string[];
  /** Message channel only. */
  [OVERCOST]?: boolean;
};

const FILTERS_BY_ENTITY = createDictionary<Partial<MetricsFiltersReducer>, Partial<MetricsFiltersReducer>>(
  {
    [BILLING_TYPE.MESSAGE]: {
      [PROVIDERS]: [],
      [OVERCOST]: false,
    },
  },
  {}
);

const getInitialState = (entity: BillingEntityEnum): MetricsFiltersReducer => ({
  ...FILTERS_BY_ENTITY[entity],
  to: endOfMonth(new Date()).toISOString(),
  from: startOfMonth(new Date()).toISOString(),
  sort: DEFAULT_SORT,
});

const filters = (entity: BillingEntityEnum, config: EntityMatcherConfig) => {
  const filterAdapter = filterSliceAdapter(getInitialState(entity));

  const onType = (type: string) => (action: Action) => config.matcher(action) && action.type === type;

  return createReducer<MetricsFiltersReducer>(filterAdapter.getInitialState(), {}, [
    {
      matcher: onType(actionTypes.SET_BILLING_DASHBOARD_FILTER),
      reducer: filterAdapter.setFilter,
    },
    {
      matcher: onType(actionTypes.SET_BILLING_DASHBOARD_FILTERS),
      reducer: filterAdapter.setFilters,
    },
    {
      matcher: onType(actionTypes.CLEAR_BILLING_DASHBOARD_FILTER),
      reducer: filterAdapter.resetFilters,
    },
    {
      matcher: onType(actionTypes.REMOVE_BILLING_DASHBOARD_FILTER),
      reducer: filterAdapter.removeFilter,
    },
  ]);
};

export default filters;
